/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "./ActionButton";

export interface IErrorFallbackProps {
	title?: string;
	message?: string;
	canReload?: boolean;
	className?: string;
}

export const ErrorFallback: React.FC<IErrorFallbackProps> = ({
	title = "Something went wrong",
	message = "An unexpected error occurred while loading the page. Please try reloading the page or contact support if the problem persists.",
	canReload = true,
	className
}) => {
	const baseClass = BEM("edt-error-fallback");
	baseClass.add("edt-base");

	const reloadPage = useCallback(() => {
		window?.location?.reload();
	}, []);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<h2>{title}</h2>
			<p>
				{message}
			</p>
			{canReload && (
				<ActionButton
					label="Reload Page"
					onClick={reloadPage}
					style={ACTION_BUTTON_STYLE.PRIMARY}
					size={ACTION_BUTTON_SIZE.LARGE}
					className={baseClass.element("button").toClassName()}
				/>
			)}
		</div>
	);
};
